/**
 * Offer Lab Landing Page for trAInedup.ai
 * Standalone registration page for publishing offers on Offer Lab
 */

import React, { useEffect } from 'react';
import { Layout } from '../components/common/Layout';
import { Button } from '../components/common/Button';
import { CheckCircle, Laptop, MessageCircle, Calendar, Users } from 'lucide-react';
import { SUBSCRIPTION_PRICES } from '../utils/constants';

/**
 * Offer Lab landing page component
 * Displays the standard offer with Offer Lab integration for payment
 */
export const OfferLabPage: React.FC = () => {
  // Load Offer Lab script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://app.offerlab.com/assets/embed/offerlab-cta.js';
    script.async = true;
    script.dataset.publishableKey = 'vTi8cHYrGIqS5LDsJwGbRzQt';
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  return (
    <Layout
      showHeader={false}
      showFooter={true}
      showUpgradeBanner={false}
      showChatbot={false}
    >
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50">
        {/* Hero Section */}
        <section className="py-8 md:py-10 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center space-y-3 md:space-y-4">
              
              <h1 className="font-montserrat font-bold text-4xl md:text-5xl text-neutral-800 leading-tight">
                Practical AI training for real people.
              </h1>
              <p className="text-lg md:text-xl text-neutral-600 max-w-2xl mx-auto md:whitespace-nowrap">
                Start learning with a plan that updates every month and is tailored to you
              </p>
              <p className="text-md text-neutral-500">No tech experience required.</p>

              {/* Features Box */}
              <div className="bg-pink-50 rounded-lg p-4 md:p-5 max-w-xl mx-auto shadow-sm">
                <ul className="space-y-2 text-left">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <span className="text-neutral-700">Personalised AI Skills Assessment</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <span className="text-neutral-700">Tailored learning plan</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <span className="text-neutral-700">Courses added monthly</span>
                  </li>

                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                    <span className="text-neutral-700">Private community for questions and support</span>
                  </li>
                </ul>
              </div>
            </div>

{/* Pricing and CTA - Moved to be inline with the rest of the content */}
              <div className="space-y-2 pt-4 text-center">
                <p className="text-xl md:text-2xl font-bold text-neutral-800">{SUBSCRIPTION_PRICES.standard.display}/month</p>
              </div>
              <div className="max-w-xs mx-auto pt-2">
                <Button
                  variant="primary"
                  size="lg"
                  className="text-lg w-full py-4"
                >
                  Get Started
                </Button>
              </div>
          </div>
        </section>

        {/* How it Works Section */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-montserrat font-bold text-3xl md:text-4xl text-center text-neutral-800 mb-12">
              How it works
            </h2>
            
            <div className="grid md:grid-cols-4 gap-8">
              {/* Step 1 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-xl">
                  1
                </div>
                <h3 className="font-montserrat font-semibold text-lg mb-3">Register for your membership</h3>
                <p className="text-neutral-600 text-sm">
                  Register for your trAIned up membership
                </p>
              </div>

              {/* Step 2 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-xl">
                  2
                </div>
                <h3 className="font-montserrat font-semibold text-lg mb-3">Take The Assessment</h3>
                <p className="text-neutral-600 text-sm">
                  Complete your Skills Assessment and tell us about your current AI knowledge and future goals to give us a clear picture.
                </p>
              </div>

              {/* Step 3 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-red-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-xl">
                  3
                </div>
                <h3 className="font-montserrat font-semibold text-lg mb-3">Get Your Tailored Plan</h3>
                <p className="text-neutral-600 text-sm">
                  Receive a personalised learning plan with courses tailored to your skill level and goals. New modules are added monthly.
                </p>
              </div>

              {/* Step 4 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 font-bold text-xl">
                  4
                </div>
                <h3 className="font-montserrat font-semibold text-lg mb-3">Start Learning</h3>
                <p className="text-neutral-600 text-sm">
                  Start with your first courses, designed to fit around your schedule, you can then begin applying AI right away.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Success Stories Section */}
        <section className="py-16 px-4 bg-gradient-to-br from-primary-50 to-secondary-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-montserrat font-bold text-3xl md:text-4xl text-center text-neutral-800 mb-4">
              Here's what happens when everyday people start using AI
            </h2>
            <p className="text-center text-neutral-600 mb-12 max-w-3xl mx-auto">
              You don't need to be a tech expert to get started. Just real goals and the right guidance. 
              Here's what a few of our members have done with the skills they learned inside trAInedup.ai
            </p>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Story 1 - Jenny */}
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xl">👩</span>
                  </div>
                  <div>
                    <h3 className="font-semibold">Jenny</h3>
                    <p className="text-sm text-neutral-500">Small Business Owner</p>
                  </div>
                </div>
                <p className="text-primary font-semibold mb-3">Transformed Operations</p>
                <p className="text-neutral-600 text-sm mb-4">
                  <strong>Before:</strong> Spending countless hours on tedious admin tasks.
                </p>
                <p className="text-neutral-600 text-sm mb-4">
                  <strong>After:</strong> Using AI to automate repetitive tasks and reclaim valuable time.
                </p>
                <p className="text-neutral-600 text-sm">
                  <strong>How he did it:</strong> Took the AI Foundations course to understand AI basics, then used AI tools to automate email responses, invoice processing, and customer data management.
                </p>
              </div>

              {/* Story 2 - Erin */}
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-xl">👨</span>
                  </div>
                  <div>
                    <h3 className="font-semibold">Erin</h3>
                    <p className="text-sm text-neutral-500">Freelance Teacher</p>
                  </div>
                </div>
                <p className="text-secondary font-semibold mb-3">60% Revenue Increase</p>
                <p className="text-neutral-600 text-sm mb-4">
                  <strong>Before:</strong> Juggling multiple projects and struggling to scale.
                </p>
                <p className="text-neutral-600 text-sm mb-4">
                  <strong>After:</strong> Scaled their freelance business and increased revenue by 60%.
                </p>
                <p className="text-neutral-600 text-sm">
                  <strong>How he did it:</strong> Learned to use AI tools for content creation, client outreach, and project management. Created AI-generated lesson plans and marketing materials that helped attract more clients.
                </p>
              </div>

              {/* Story 3 - Paul */}
              <div className="bg-white rounded-lg p-6 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-xl">👨‍💼</span>
                  </div>
                  <div>
                    <h3 className="font-semibold">Paul</h3>
                    <p className="text-sm text-neutral-500">Business Owner</p>
                  </div>
                </div>
                <p className="text-accent font-semibold mb-3">Modernised Family Business</p>
                <p className="text-neutral-600 text-sm mb-4">
                  <strong>Before:</strong> Running a traditional family business with outdated processes.
                </p>
                <p className="text-neutral-600 text-sm mb-4">
                  <strong>After:</strong> Modernised operations using simple AI tools, improved customer experience, and increased profits.
                </p>
                <p className="text-neutral-600 text-sm">
                  <strong>How he did it:</strong> Implemented AI-powered customer service chatbots, automated inventory management, and used AI for market analysis to stay competitive.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Guarantee Section */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-montserrat font-bold text-3xl md:text-4xl text-center text-neutral-800 mb-12">
              The trAIned up Guarantee
            </h2>
            
            <div className="grid md:grid-cols-5 gap-8">
              {/* Personal training plan */}
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-montserrat font-semibold text-lg mb-2">Personal training plan</h3>
                <p className="text-neutral-600 text-sm">
                  A tailored learning path based on your goals
                </p>
              </div>

              {/* Practical courses */}
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Laptop className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-montserrat font-semibold text-lg mb-2">Practical courses</h3>
                <p className="text-neutral-600 text-sm">
                  Lessons you can apply in real situations
                </p>
              </div>

              {/* No confusing jargon */}
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-montserrat font-semibold text-lg mb-2">No confusing jargon</h3>
                <p className="text-neutral-600 text-sm">
                  Clear explanations written for real people
                </p>
              </div>

              {/* 7 day money back */}
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-montserrat font-semibold text-lg mb-2">7 day money back</h3>
                <p className="text-neutral-600 text-sm">
                  Try it risk free. Cancel within 7 days for a refund
                </p>
              </div>

              {/* Supportive community */}
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-montserrat font-semibold text-lg mb-2">Supportive community</h3>
                <p className="text-neutral-600 text-sm">
                  Ask questions and learn alongside others
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-primary to-secondary">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-montserrat font-bold text-3xl md:text-4xl text-white mb-6">
              Ready to See What AI Can Do for You?
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-3xl mx-auto">
              Join thousands who are already using AI to save time and money, increase productivity, and stay ahead of the curve. Register for free and complete your Skills Assessment
            </p>
            <Button 
              variant="primary" 
              size="lg"
              className="!bg-white !text-primary-600 hover:!bg-gray-100 text-lg px-12 py-4"
            >
              Get Started
            </Button>
            

          </div>
        </section>
      </div>
    </Layout>
  );
};
